import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, useCallback, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import PublicationListsBrowse from "./components/PublicationListsBrowse";
import SetListDomains from "./components/SetListDomains";
import api from "../../../api";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { PublicationListSet } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { areSameIdSets, getSelectionFromLists, resolveSetLists } from "./publicationListSetUtils";
import {
  setSelectedDomainIds,
  setSelectedDomainListsIds,
  setSelectedDomainNames,
  setSelectedPublicationListSetId,
} from "../../../redux/reducers/campaignToCreate";
import { usePublicationLists } from "./usePublicationLists";

type ActiveTab = "set" | "manual";

// ─── Styled components ───────────────────────────────────────────────────────

const PanelRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const TabBar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  gap: 4,
  padding: 4,
  borderRadius: 10,
  background: theme.palette.brand.gray100,
  alignSelf: "flex-start",
}));

const TabButton = styled("button")(({ theme }: { theme: Theme }) => ({
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: theme.palette.brand.gray500,
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 16px",
  borderRadius: 8,
  transition: "all 0.15s ease",
  "&[data-active='true']": {
    background: theme.palette.brand.white,
    color: theme.palette.brand.gray900,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
}));

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
}));

const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));

const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const CountCell = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));

const Radio = styled("div")(({ theme }: { theme: Theme }) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `2px solid ${theme.palette.brand.gray300}`,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&[data-selected='true']": { borderColor: theme.palette.brand.coral },
  "& span": {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: theme.palette.brand.coral,
  },
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "32px 20px",
  textAlign: "center" as const,
  fontSize: 14,
  color: theme.palette.brand.gray500,
  "& a": {
    color: theme.palette.brand.coral,
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": { textDecoration: "underline" },
  },
}));

const LoadingState = styled("div")({
  padding: "32px",
  display: "flex",
  justifyContent: "center",
});

const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "16px 0",
}));

// ─── Component ───────────────────────────────────────────────────────────────

type PublicationListSetSelectorPanelProps = {
  panelMode?: boolean;
};

const PublicationListSetSelectorPanel: FC<PublicationListSetSelectorPanelProps> = ({ panelMode = false }) => {
  const dispatch = useDispatch();
  const { selectedAdvertiserId, allLists, isLoadingPublic, isLoadingCustom } = usePublicationLists();
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const selectedSetId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedPublicationListSetId,
  );
  const selectedListIds = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedDomainListsIds);
  const selectedDomainIds = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedDomainIds);

  const [activeTab, setActiveTab] = useState<ActiveTab>("set");
  const [sets, setSets] = useState<PublicationListSet[]>([]);
  const [areSetsLoaded, setAreSetsLoaded] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [hasMatchedRestoredSelection, setHasMatchedRestoredSelection] = useState(false);

  const isLoading = !areSetsLoaded || isLoadingPublic || isLoadingCustom;

  // ─── Load sets for the selected advertiser ────────────────────────────────
  useEffect(() => {
    if (!selectedAdvertiserId) {
      setSets([]);
      return;
    }
    setAreSetsLoaded(false);
    const fetch = async () => {
      try {
        const { payload } = await api.getPublicationListSets(selectedAdvertiserId);
        setSets(Array.isArray(payload) ? payload : []);
      } catch {
        setSets([]);
      } finally {
        setAreSetsLoaded(true);
      }
    };
    fetch();
  }, [selectedAdvertiserId]);

  // ─── Restore: a template gives us list IDs but no set ID ──────────────────
  // Runs exactly once, as soon as the sets are known — after that the tabs own
  // the selection and must not be second-guessed.
  useEffect(() => {
    if (hasMatchedRestoredSelection || !areSetsLoaded) return;
    setHasMatchedRestoredSelection(true);
    if (selectedSetId || selectedListIds.length === 0) return;
    const match = sets.find((set) => areSameIdSets(set.publicationListIds, selectedListIds));
    if (match) {
      dispatch(setSelectedPublicationListSetId(match.id));
      setActiveTab("set");
    } else {
      setActiveTab("manual");
    }
  }, [areSetsLoaded, dispatch, hasMatchedRestoredSelection, selectedListIds, selectedSetId, sets]);

  // ─── Selection ────────────────────────────────────────────────────────────
  const applySelection = useCallback(
    (listIds: string[], domainIds: string[], domainNames: string[]) => {
      dispatch(setSelectedDomainListsIds(listIds));
      dispatch(setSelectedDomainIds(domainIds));
      dispatch(setSelectedDomainNames(domainNames));
    },
    [dispatch],
  );

  const handleSelectSet = (set: PublicationListSet) => {
    if (selectedSetId === set.id) {
      dispatch(setSelectedPublicationListSetId(null));
      applySelection([], [], []);
      return;
    }
    const { listIds, domainIds, domainNames } = getSelectionFromLists(
      resolveSetLists(allLists, set.publicationListIds),
    );
    dispatch(setSelectedPublicationListSetId(set.id));
    applySelection(listIds, domainIds, domainNames);
  };

  /** Manual tab: toggling a list rebuilds the whole selection from the checked lists. */
  const handleToggleList = (listId: string) => {
    const nextListIds = selectedListIds.includes(listId)
      ? selectedListIds.filter((id) => id !== listId)
      : [...selectedListIds, listId];
    const { listIds, domainIds, domainNames } = getSelectionFromLists(resolveSetLists(allLists, nextListIds));
    applySelection(listIds, domainIds, domainNames);
  };

  const handleTabChange = (tab: ActiveTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    // Each tab owns the selection while it is active — switching starts clean.
    dispatch(setSelectedPublicationListSetId(null));
    applySelection([], [], []);
  };

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!selectedAdvertiserId) {
    return <AdvertiserPrompt>Please select an advertiser to load publication list sets.</AdvertiserPrompt>;
  }

  return (
    <PanelRoot>
      {panelMode && (
        <ManageLink>
          To create or manage publication list sets, go to{" "}
          <a href="/publication-lists" target="_blank" rel="noopener noreferrer">
            Publication Lists ↗
          </a>
          .
        </ManageLink>
      )}

      <TabBar>
        <TabButton type="button" data-active={activeTab === "set"} onClick={() => handleTabChange("set")}>
          Saved set
        </TabButton>
        <TabButton type="button" data-active={activeTab === "manual"} onClick={() => handleTabChange("manual")}>
          Choose manually
        </TabButton>
      </TabBar>

      {activeTab === "manual" ? (
        <PublicationListsBrowse
          selectionMode
          selectedListIds={selectedListIds}
          selectedDomainIds={selectedDomainIds}
          onListSelect={handleToggleList}
        />
      ) : isLoading ? (
        <LoadingState>
          <CircularProgress size={28} />
        </LoadingState>
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>
            No publication list sets found for <strong>{selectedAdvertiser?.name}</strong>.{" "}
            <a href="/publication-lists" target="_blank" rel="noopener noreferrer">
              Go to Publication Lists →
            </a>
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th style={{ width: 60 }} />
                <Th>Name</Th>
                <Th>Lists</Th>
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const isSelected = selectedSetId === set.id;
                const memberLists = resolveSetLists(allLists, set.publicationListIds);
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow data-selected={String(isSelected)} onClick={() => toggleExpanded(set.id)}>
                      <Td
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSet(set);
                        }}
                      >
                        <Radio data-selected={String(isSelected)}>{isSelected && <span />}</Radio>
                      </Td>
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {memberLists.length.toLocaleString()}
                        </CountCell>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={3}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <SetListDomains lists={memberLists} />
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}
    </PanelRoot>
  );
};

export default PublicationListSetSelectorPanel;
